import { ShipmentListDTO } from '@uturn/api/v1';
import { type GetShipmentsParams } from '@uturn/api/v3';
import { ShipmentView } from '@uturn/portal/hooks/shipments/use-shipment-view';
import { useShipmentsSearchStore } from '@uturn/portal/store/shipments/shipments-search';
import { ComponentProps, FC, Suspense, useContext } from 'react';
import { ShipmentGroupDetailContext } from '../providers/shipment-group-detail';
import { ShipmentViewContext } from '../providers/shipment-view';
import { ShipmentFilter } from './filters';
import { GroupedShipments } from './grouped-shipments';
import { ShipmentCard } from './shipment-card';
import { ShipmentSkeletonLoader } from './shipment-skeleton';
import ShipmentsTable from './shipments-table';
import { UngroupedShipments } from './ungrouped-shipments';

export type ShipmentsProps = {
	shipmentsParams: GetShipmentsParams;
	details: ShipmentPageProps['details'];
	EmptyState: FC;
	hasQuotes?: boolean;
};

type ShipmentDetailsProp = ComponentProps<typeof ShipmentCard>['details'];

export type ShipmentPageProps = {
	EmptyState: FC;
	details: (shipment: ShipmentListDTO) => ShipmentDetailsProp; // TODO: Store this in the ShipmentsSearchStore instead?
	hasQuotes?: boolean;
	enableGroupFilter: boolean;
	/** @deprecated. */
	enableTsoFilter?: boolean;
	search: Partial<GetShipmentsParams>;
	filterOnChange: (filters: Partial<GetShipmentsParams>) => void;
};

export function ShipmentPage({
	EmptyState,
	details,
	hasQuotes,
	enableGroupFilter,
	enableTsoFilter = false,
	search,
	filterOnChange,
}: ShipmentPageProps) {
	const { activeStatusTab, defaultFilters, shipmentsSearchFilters } =
		useShipmentsSearchStore();
	const { groupId } = useContext(ShipmentGroupDetailContext);
	const view = useContext(ShipmentViewContext);

	if (view === ShipmentView.Table) {
		return (
			<ShipmentsTable
				filterStatuses={defaultFilters['filter.statuses']}
				/**
				 * In the shipments table we don't support the tab "groups"
				 * so we just set the status tab to undefined to let the component handle the default use case
				 */
				statusTab={activeStatusTab !== 'groups' ? activeStatusTab : undefined}
			/>
		);
	}

	const shipmentsProps: ShipmentsProps = {
		details,
		shipmentsParams: shipmentsSearchFilters,
		EmptyState: EmptyState,
		hasQuotes: hasQuotes,
	};

	const emptyStatusSelection =
		Array.isArray(shipmentsSearchFilters['filter.statuses']) &&
		shipmentsSearchFilters['filter.statuses'].length === 0;

	const renderShipments = () => {
		if (emptyStatusSelection) {
			return <EmptyState />;
		}
		if (groupId !== '') {
			return <GroupedShipments {...shipmentsProps} />;
		}
		return <UngroupedShipments {...shipmentsProps} />;
	};

	return (
		<div className="grid grid-cols-1 gap-6 pt-2 @4xl/content:grid-cols-[minmax(0,_1fr)_minmax(0,_3fr)]">
			<ShipmentFilter
				enableGroupFilter={enableGroupFilter}
				enableTsoFilter={enableTsoFilter}
				initialFilters={search}
				onChange={filterOnChange}
			/>
			<div className="flex flex-col gap-3">
				<Suspense fallback={<ShipmentSkeletonLoader />}>
					{renderShipments()}
				</Suspense>
			</div>
		</div>
	);
}
